import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import Table from "../../../Components/UiComponents/Table/Table";
import LoadingOverlay from "../ExpectedTrackForm/LoadingOverlay";
import { getData } from "../../../Services/Services";
import { getallforuserdet } from "../../../Services/EndPoints";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Download, FileTextIcon, Sheet, Search } from "lucide-react";
import "../UserRegistrationFace/FaceManagement.css";


const UserFaceDetectionDetails = () => {
    const [faceDetectionData, setFaceDetectionData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const itemsPerPage = 10;
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const filteredData = useMemo(() => {
        if (!searchTerm.trim()) return faceDetectionData;
        const lower = searchTerm.toLowerCase();
        return faceDetectionData.filter(row =>
            (row.personName || "").toLowerCase().includes(lower) ||
            (row.roleType || "").toLowerCase().includes(lower) ||
            (row.department || "").toLowerCase().includes(lower) ||
            (row.locationName || "").toLowerCase().includes(lower) ||
            (row.status || "").toLowerCase().includes(lower)
        );
    }, [faceDetectionData, searchTerm]);


    useEffect(() => {
        fetchFaceDetectionData();
    }, []);

    const fetchFaceDetectionData = async () => {
        try {
            setIsLoading(true);

            const response = await getData(getallforuserdet);

            console.log("Response:", response);

            if (response && response.status === 'success') {
                toast.success(response.msg || 'Data fetched successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                setFaceDetectionData(response.data || []);
            } else {
                toast.error(response.msg || 'Failed to fetch data', {
                    position: 'top-center',
                    autoClose: 3000
                });
                setFaceDetectionData([]);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch data. Please try again.', {
                position: 'top-center',
                autoClose: 3000
            });
            setFaceDetectionData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const formatTimestamp = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${day}-${month}-${year}, ${hours}:${minutes} ${ampm}`;
    };

    const downloadExcel = async () => {
        if (!faceDetectionData || faceDetectionData.length === 0) {
            toast.warning('No data available to download', {
                position: 'top-center',
                autoClose: 3000
            });
            return;
        }

        setIsLoading(true);

        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Face Detection Details');

            worksheet.columns = [
                { header: 'Person Name', key: 'personName', width: 25 },
                { header: 'Role/Type', key: 'roleType', width: 25 },
                { header: 'Department', key: 'department', width: 20 },
                { header: 'Device', key: 'device', width: 20 },
                { header: 'Log Time', key: 'logTime', width: 25 },
                { header: 'Confidence', key: 'confidence', width: 15 },
                { header: 'Status', key: 'status', width: 15 },
                { header: 'Media Path URL', key: 'mediaPath', width: 60 }
            ];

            worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
            worksheet.getRow(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF18a3a0' }
            };
            worksheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center' };
            worksheet.getRow(1).height = 25;

            // Loop through all face data and add hyperlinks
            faceDetectionData.forEach((row, index) => {
                const rowNumber = index + 2; // +2 because Excel rows are 1-indexed and we have a header

                worksheet.addRow({
                    personName: row.personName || '',
                    roleType: row.roleType || 'N/A',
                    department: row.department || 'N/A',
                    device: row.locationName || 'N/A',
                    logTime: formatTimestamp(row.logTime),
                    confidence: row.confidence ? `${row.confidence}%` : 'N/A',
                    status: row.status || '',
                    mediaPath: row.mediaPath || 'N/A'
                });

                // Add hyperlink to the Media Path URL cell if URL exists
                if (row.mediaPath && row.mediaPath !== 'N/A') {
                    const cell = worksheet.getCell(`H${rowNumber}`); // Column G is Media Path URL
                    cell.value = {
                        text: row.mediaPath,
                        hyperlink: row.mediaPath
                    };
                    cell.font = {
                        color: { argb: 'FF0563C1' }, // Blue color for links
                        underline: true
                    };
                }
            });

            // Generate filename
            const date = new Date();
            const filename = `Face_Detection_Report_${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}.xlsx`;

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create download link with proper attributes
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.setAttribute('target', '_self'); // Prevent opening in new tab

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            setTimeout(() => {
                window.URL.revokeObjectURL(url);
            }, 100);

            toast.success('Report downloaded successfully', {
                position: 'top-center',
                autoClose: 3000
            });
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error('Failed to download report', {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const downloadPDF = async () => {
        if (!faceDetectionData || faceDetectionData.length === 0) {
            toast.warning('No data available to download', {
                position: 'top-center',
                autoClose: 3000
            });
            return;
        }

        setIsLoading(true);

        try {
            const doc = new jsPDF("l", "pt", "a4");
            let currentY = 50;

            let fontName = "Helvetica";
            doc.setFont(fontName);
            doc.setFontSize(13);

            const pdfTitle = "Face Detection Details Report";

            const pageWidth = doc.internal.pageSize.width;
            const xOffset = pageWidth / 2;
            doc.text(pdfTitle, xOffset, currentY, { align: "center" });
            currentY += 30;

            const tableHeaders = [
                "Person Name",
                "Role/Type",
                "Department",
                "Device",
                "Log Time",
                "Confidence",
                "Status",
                "Media Path URL"
            ];

            const tableData = faceDetectionData.map(row => [
                row.personName || "NA",
                row.roleType || "N/A",
                row.department || "N/A",
                row.locationName || "N/A",
                formatTimestamp(row.logTime) || "NA",
                row.confidence ? `${row.confidence}%` : "N/A",
                row.status || "NA",
                row.mediaPath || "N/A"
            ]);

            doc.autoTable({
                head: [tableHeaders],
                body: tableData,
                startY: currentY,
                theme: "grid",
                headStyles: {
                    fillColor: [24, 163, 160],
                    textColor: [255, 255, 255],
                    fontStyle: "bold",
                    halign: "center",
                    fontSize: 10,
                },
                bodyStyles: {
                    fontSize: 9,
                },
                columnStyles: {
                    // Total must fit A4 landscape (841.89pt) minus 20pt margins each side (~801pt)
                    0: { cellWidth: 100 },
                    1: { cellWidth: 65 },
                    2: { cellWidth: 70 },
                    3: { cellWidth: 75 },
                    4: { cellWidth: 105 },
                    5: { cellWidth: 62, halign: 'center' },
                    6: { cellWidth: 58, halign: 'center' },
                    7: { cellWidth: 265 }
                },
                margin: { left: 20, right: 20 },
                tableWidth: "auto",
                didDrawCell: function (data) {
                    // Add clickable links to Media Path URL column (column index 7)
                    if (data.section === 'body' && data.column.index === 7) {
                        const url = faceDetectionData[data.row.index]?.mediaPath;
                        if (url && url !== 'N/A') {
                            doc.link(
                                data.cell.x,
                                data.cell.y,
                                data.cell.width,
                                data.cell.height,
                                { url: url }
                            );
                        }
                    }
                },
                didParseCell: function (data) {
                    if (data.section === "body") {
                        const cellValue = data.cell.raw;

                        // Make Media Path URL text blue and underlined
                        if (data.column.index === 7 && cellValue !== 'N/A') {
                            data.cell.styles.textColor = [5, 99, 193]; // Blue color
                            data.cell.styles.fontStyle = 'normal';
                        }

                        if (data.column.index === 5 || data.column.index === 6) {
                            data.cell.styles.halign = "center";
                        } else if (
                            typeof cellValue === "number" ||
                            (typeof cellValue === "string" &&
                                !isNaN(cellValue) &&
                                !isNaN(parseFloat(cellValue)) &&
                                cellValue.trim() !== "")
                        ) {
                            data.cell.styles.halign = "center";
                        } else {
                            data.cell.styles.halign = "left";
                        }
                    }
                },
            });

            // **FOOTER SECTION WITH PAGE BREAK LOGIC**
            const pageHeight = doc.internal.pageSize.height;
            const footerHeight = 180;
            const finalY = doc.lastAutoTable.finalY || currentY;

            if (finalY + footerHeight > pageHeight) {
                doc.addPage();
                currentY = 50;
            } else {
                currentY = finalY;
            }

            let footerY = pageHeight - 180;

            doc.setFontSize(10);

            // Left side footer items
    doc.text("Letter No. :", 50, footerY + 100);
    doc.text("Date :", 50, footerY + 120);
    doc.text("Copy :", 50, footerY + 140);

    // Right side footer items
    doc.text("XXXX", 550, footerY + 100);
    doc.text("XXXXXXXXX", 550, footerY + 120);
    doc.text("XXXXXX", 550, footerY + 140);

            const now = new Date();
            // doc.text(formatTimestamp(now), 200, footerY + 100);
            // doc.text(faceDetectionData.length.toString(), 200, footerY + 120);
            doc.text("", 200, footerY + 140);

            const istDate = now.toISOString().split("T")[0].replace(/-/g, "");
            const hours = now.getHours().toString().padStart(2, "0");
            const minutes = now.getMinutes().toString().padStart(2, "0");
            const seconds = now.getSeconds().toString().padStart(2, "0");
            const istTime = `${hours}${minutes}${seconds}`;
            const filename = `Face_Detection_Report_${istDate}_${istTime}.pdf`;

            // Use save method instead of creating a link element
            doc.save(filename);

            toast.success('PDF downloaded successfully', {
                position: 'top-center',
                autoClose: 3000
            });
        } catch (error) {
            console.error("Error generating PDF:", error);
            toast.error("Error generating PDF. Please try again.", {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            setIsLoading(false);
        }
    };


    const columns = React.useMemo(
        () => [
            {
                Header: "Face",
                accessor: "mediaPath",
                disableSortBy: true,
                Cell: ({ row }) => {
                    const imageUrl = row.original.mediaPath || row.original.image || row.original.photo;
                    return (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            padding: '4px'
                        }}>
                            {imageUrl ? (
                                <img
                                    src={imageUrl}
                                    alt="Face"
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '8px',
                                        objectFit: 'cover',
                                        border: '2px solid #e0e0e0',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {
                                        setSelectedImage(imageUrl);
                                        setShowImageModal(true);
                                    }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        borderRadius: '50%',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        color: '#666',
                                        border: '2px solid #e0e0e0'
                                    }}
                                >
                                    No Image
                                </div>
                            )}
                            <div
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f0f0f0',
                                    display: 'none',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '12px',
                                    color: '#666',
                                    border: '2px solid #e0e0e0'
                                }}
                            >
                                No Image
                            </div>
                        </div>
                    );
                }
            },
            {
                Header: "Person Name",
                accessor: "personName",
                disableSortBy: true
            },

            {
                Header: "Role/Type",
                accessor: "roleType",
                disableSortBy: true,
                Cell: ({ value }) => (
                    <span style={{
                        color: value ? '#333' : '#6c757d',
                        fontStyle: value ? 'normal' : 'normal'
                    }}>
                        {value || 'N/A'}
                    </span>
                )
            },
            {
                Header: "Department",
                accessor: "department",
                disableSortBy: true,
                Cell: ({ value }) => (
                    <span style={{
                        color: value ? '#333' : '#6c757d',
                        fontStyle: value ? 'normal' : 'normal'
                    }}>
                        {value || 'N/A'}
                    </span>
                )
            },
            {
                Header: "Device",
                accessor: "locationName",
                disableSortBy: true,
                Cell: ({ value }) => (
                    <span style={{
                        color: value ? '#333' : '#6c757d',
                        fontStyle: value ? 'normal' : 'normal'
                    }}>
                        {value || 'N/A'}
                    </span>
                )
            },
            {
                Header: "Log Time",
                accessor: "logTime",
                Cell: ({ value }) => formatTimestamp(value)
            },
            {
                Header: "Confidence",
                accessor: "confidence",
                disableSortBy: true,
                Cell: ({ value }) => {
                    const confidenceValue = `${value}%` || '0%';
                    const numValue = parseFloat(confidenceValue.toString().replace('%', ''));

                    let bgColor = '';
                    let textColor = '';

                    if (numValue >= 80) {
                        bgColor = '#d1fae5';
                        textColor = '#065f46';
                    } else if (numValue >= 60) {
                        bgColor = '#fef3c7';
                        textColor = '#92400e';
                    } else {
                        bgColor = '#fee2e2';
                        textColor = '#991b1b';
                    }

                    return (
                        <span
                            style={{
                                backgroundColor: bgColor,
                                color: textColor,
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                display: 'inline-block'
                            }}
                        >
                            {confidenceValue}
                        </span>
                    );
                }
            },
            {
                Header: "Status",
                accessor: "status",
                disableSortBy: true,
                Cell: ({ value }) => {
                    let bgColor = '';
                    let textColor = '';

                    switch (value?.toLowerCase()) {
                        case 'recognized':
                            bgColor = '#d1fae5';
                            textColor = '#065f46';
                            break;
                        case 'unknown':
                            bgColor = '#fee2e2';
                            textColor = '#991b1b';
                            break;
                        default:
                            bgColor = '#e0e7ff';
                            textColor = '#3730a3';
                            break;
                    }

                    return (
                        <span
                            style={{
                                backgroundColor: bgColor,
                                color: textColor,
                                padding: '5px',
                                borderRadius: '12px',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                display: 'inline-block',
                                textTransform: 'capitalize'
                            }}
                        >
                            {value}
                        </span>
                    );
                }
            },
            {
                Header: "Recognition View",
                accessor: "media",
                disableSortBy: true,
                Cell: ({ row }) => (
                    <button
                        className="btn"
                        title="View Recognition Details"
                        onClick={() => {
                            setSelectedRow(row.original);
                            setShowDetailsModal(true);
                        }}
                        style={{

                        }}
                    >
                        <i className="bi bi-eye"></i>
                    </button>
                )
            }
        ],
        []
    );

    return (
        <div className="row justify-content-center fm-detection-root">
            <div className="col-12">
                {isLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <LoadingOverlay message="Loading data..." />
                    </div>
                ) : (
                    <div className="card shadow-sm fm-detection-card">
                        {/* ── Toolbar ── */}
                        <div className="fd-toolbar">
                            {/* LEFT: count badge */}
                            <div className="fd-toolbar-left">
                                <span className="fd-count-label">
                                    {filteredData.length} / {faceDetectionData.length} records
                                </span>
                            </div>
                            {/* RIGHT: download buttons + search */}
                            <div className="fd-toolbar-right">
                                <button
                                    className="fm-btn-excel fd-dl-btn"
                                    onClick={downloadExcel}
                                    disabled={isLoading || faceDetectionData.length === 0}
                                    title="Export to Excel"
                                >
                                    <Download size={13} />
                                    <Sheet size={13} />
                                    <span>Excel</span>
                                </button>
                                <button
                                    className="fm-btn-pdf fd-dl-btn"
                                    onClick={downloadPDF}
                                    disabled={isLoading || faceDetectionData.length === 0}
                                    title="Export to PDF"
                                >
                                    <Download size={13} />
                                    <FileTextIcon size={13} />
                                    <span>PDF</span>
                                </button>
                                <div className="fd-search-wrap">
                                    <Search size={14} className="fd-search-icon" />
                                    <input
                                        className="fd-search-input"
                                        placeholder="Search by name, role, department…"
                                        title="Search by name, role, department, device or status"
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* ── Table ── */}
                        <Table
                            data={filteredData}
                            title=""
                            showFilter={false}
                            columns={columns}
                            numberOfRows={itemsPerPage}
                            showExport={false}
                            showReset={false}
                            onExpand={false}
                            showSearch={false}
                            isHeaderFixed={true}
                            rowCount={filteredData.length}
                            headerTableHeadStyle={{
                                background: "#1a7a73",
                                color: "#FFFFFF"
                            }}
                            tableStyle={{
                                border: "none",
                                borderRadius: "0",
                                padding: "0"
                            }}
                        />
                    </div>
                )}
            </div>
            {/* Image Preview Modal */}
            {showImageModal && (
                <div
                    className="modal fade show fm-modal-enhanced"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: "200px", width: "20%" }}>
                        <div className="modal-content" >
                            <div className="modal-header" style={{ padding: "1rem 1.5rem" }}>
                                <h5 className="modal-title" style={{ fontSize: "1rem" }}>Image Preview</h5>
                            </div>
                            <div
                                className="modal-body text-center"
                                style={{
                                    padding: "0.5rem 1rem",
                                    maxHeight: "60vh",
                                    overflowY: "auto",
                                    display: "flex",
                                    justifyContent: "center",
                                }}
                            >
                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt="Preview"
                                        className="img-fluid rounded shadow"
                                        style={{
                                            maxWidth: "100%",
                                            maxHeight: "50vh",
                                            objectFit: "contain",
                                        }}
                                    />
                                ) : (
                                    <p>No image available</p>
                                )}
                            </div>

                            <div className="modal-footer" style={{ padding: "0.25rem 1rem" }}>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setShowImageModal(false)}
                                    style={{
                                        padding: "0.25rem 0.75rem",
                                        fontSize: "0.875rem",
                                        height: "auto"
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Face Recognition Details Modal */}
            {showDetailsModal && selectedRow && (
                <div
                    className="modal fade show fm-details-modal"
                    style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                    tabIndex="-1"
                    role="dialog"
                >
                    <div className="modal-dialog modal-dialog-centered" role="document" style={{ maxWidth: "500px" }}>
                        <div className="modal-content">
                            {/* Modal Header */}
                            <div className="fm-details-modal-header">
                                <h5 style={{
                                    fontWeight: "600",
                                    fontSize: "15px",
                                    color: "#ffffff",
                                    margin: 0,
                                    letterSpacing: "0.3px"
                                }}>
                                    Facial Recognition Result
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowDetailsModal(false)}
                                    style={{
                                        position: "absolute",
                                        top: "16px",
                                        right: "20px",
                                        fontSize: "12px"
                                    }}
                                ></button>
                            </div>
                            {/* Modal Body */}
                            <div className="modal-body" style={{
                                padding: "24px 20px",
                                backgroundColor: "#ffffff"
                            }}>
                                {/* Images Section */}
                                <div className="fm-face-compare-container" style={{ marginTop: '-4px' }}>
                                    {/* Left Image - Detected Face */}
                                    <div className="text-center">
                                        {(selectedRow.mediaPath || selectedRow.image) ? (
                                            <img
                                                src={selectedRow.mediaPath || selectedRow.image}
                                                alt="Detected Face"
                                                className="fm-face-img"
                                                style={{
                                                    width: "110px",
                                                    height: "110px",
                                                    objectFit: "cover"
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "110px",
                                                    height: "110px",
                                                    backgroundColor: "#f0f4ff",
                                                    borderRadius: "10px",
                                                    border: "2px solid #c7d2fe"
                                                }}
                                            >
                                                <span style={{ fontSize: "11px", color: "#6366f1" }}>No Image</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Arrow */}
                                    <div>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M5 12h14M12 5l7 7-7 7" />
                                        </svg>
                                    </div>

                                    {/* Right Image - Match From Database */}
                                    <div className="text-center">
                                        {(selectedRow.originalImage || selectedRow.image) ? (
                                            <img
                                                src={selectedRow.originalImage || selectedRow.image}
                                                alt="Match From Database"
                                                className="fm-face-img"
                                                style={{
                                                    width: "110px",
                                                    height: "110px",
                                                    objectFit: "cover"
                                                }}
                                            />
                                        ) : (
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "110px",
                                                    height: "110px",
                                                    backgroundColor: "#f0f4ff",
                                                    borderRadius: "10px",
                                                    border: "2px solid #c7d2fe"
                                                }}
                                            >
                                                <span style={{ fontSize: "11px", color: "#6366f1" }}>No Match</span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Labels and Names Row */}
                                <div className="d-flex" style={{ padding: "0 8px", marginBottom: "16px" }}>
                                    <div className="text-center" style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: "11px",
                                            color: "#64748b",
                                            marginBottom: "4px",
                                            fontWeight: "400"
                                        }}>
                                            Name
                                        </div>
                                        <div style={{
                                            fontWeight: "600",
                                            fontSize: "14px",
                                            color: "#1e293b"
                                        }}>
                                            {selectedRow.personName || 'Unknown'}
                                        </div>
                                    </div>

                                    <div style={{ width: "20px" }}></div>

                                    <div className="text-center" style={{ flex: 1 }}>
                                        <div style={{
                                            fontSize: "11px",
                                            color: "#64748b",
                                            marginBottom: "4px",
                                            fontWeight: "400"
                                        }}>
                                            Match from
                                        </div>
                                        <div style={{
                                            fontWeight: "600",
                                            fontSize: "14px",
                                            color: "#1e293b"
                                        }}>
                                            {selectedRow.personName || selectedRow.device || 'Driver'}
                                        </div>
                                    </div>
                                </div>

                                {/* Match Confidence */}
                                <div className="fm-info-row mb-2">
                                    <span className="fm-info-label">Match Confidence</span>
                                    <span className="fm-info-value">{selectedRow.confidence || '0%'}</span>
                                </div>

                                {/* Timestamp */}
                                <div className="fm-info-row mb-4">
                                    <span className="fm-info-label">Timestamp</span>
                                    <span className="fm-info-value">
                                        {formatTimestamp(selectedRow.logTime) || '2025-11-05 10:32 AM'}
                                    </span>
                                </div>

                                {/* Match Status Button */}
                                <div className="text-center" style={{ marginTop: "20px" }}>
                                    <button
                                        className="btn"
                                        style={{
                                            background: selectedRow.status?.toLowerCase() === 'recognized'
                                                ? 'linear-gradient(135deg, #10B981, #059669)'
                                                : 'linear-gradient(135deg, #EF4444, #dc2626)',
                                            color: 'white',
                                            padding: '10px 32px',
                                            borderRadius: '8px',
                                            border: 'none',
                                            fontWeight: '500',
                                            fontSize: "13px",
                                            textTransform: 'capitalize',
                                            boxShadow: selectedRow.status?.toLowerCase() === 'recognized'
                                                ? '0 4px 12px rgba(16,185,129,0.35)'
                                                : '0 4px 12px rgba(239,68,68,0.35)',
                                            cursor: 'default',
                                            letterSpacing: '0.3px'
                                        }}
                                    >
                                        {selectedRow.status?.toLowerCase() === 'recognized' ? 'Match Found' : 'No Match Found'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default UserFaceDetectionDetails;